import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Styles } from './style';
import { useNavigate, useParams } from 'react-router-dom';
import AuthenticatedLayout from '@screens/Authenticated/Layout/Layout';
import { ImageUploader, useImageUploader } from '@components/ImageUploader';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import RichTextInput from '@components/RichTextInput';
import CurrencyMaskedInput from '@components/CurrencyMaskedInput';
import { useViaCepStore } from '@stores/ViaCep/ViaCep';
import { useMetadataStore } from '@stores/Metadata/Metadata';
import { useUserStore } from '@stores/User/User';
import { UserRole } from '@stores/User/types';
import { usePropertyStore } from '@stores/Property/Property';
import { usePropertyFormDraftStore } from '@stores/PropertyFormDraft/PropertyFormDraft';
import {
  IAuthenticatedPropertyDetails,
  IDadosComercializacao,
  IDadosLocalizacao,
  IDadosPagamento,
  IDadosPrivados,
  IPropertyCharacteristic
} from '@stores/Property/types';

const FORM_DRAFT_STORAGE_PREFIX = 'property-form-draft';
const FORM_DRAFT_SAVE_DEBOUNCE_MS = 800;
const DESCRIPTION_HEIGHT_SAVE_DEBOUNCE_MS = 800;

const NewRegisterScreen: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const [mustFetchProperty, setMustFetchProperty] = useState(
    id !== undefined && id !== ''
  );

  const [didFetchProperty, setDidFetchProperty] = useState(false);

  const {
    fetchAuthenticatedPropertyDetails,
    postNewProperty,
    patchEditProperty,
    checkCanEdit
  } = usePropertyStore();
  const { fetchCep } = useViaCepStore();
  const {
    caracteristicas,
    chaves,
    cidades,
    codigosImovel,
    condicoesImovel,
    estados,
    tiposImovel,
    fetchCaracteristicas,
    fetchChaves,
    fetchCidades,
    fetchCodigosImovel,
    fetchCondicoesImovel,
    fetchTiposImovel
  } = useMetadataStore();
  const { role, user, saveEditorPreferences, fetchMyProfile } = useUserStore();
  const { corretores, fetchCorretores } = useUserStore();
  const isAdmin = role === UserRole.ADMIN;
  const isBrokerOrSecretary =
    role === UserRole.CORRETOR || role === UserRole.SECRETARIA;
  const isEditingExistingProperty = Boolean(id && id !== '');

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Helpers
  const sortByOrdem = <T extends { ordem: number }>(arr: T[]) =>
    [...arr].sort((a, b) => a.ordem - b.ordem);

  const tiposOptions = useMemo(() => sortByOrdem(tiposImovel), [tiposImovel]);
  const codigosOptions = useMemo(
    () => sortByOrdem(codigosImovel),
    [codigosImovel]
  );
  const chavesOptions = useMemo(() => sortByOrdem(chaves), [chaves]);
  const condicoesOptions = useMemo(
    () => sortByOrdem(condicoesImovel),
    [condicoesImovel]
  );
  const cidadesOptions = useMemo(() => sortByOrdem(cidades), [cidades]);
  const estadosOptions = useMemo(() => sortByOrdem(estados), [estados]);
  const caracteristicasOptions = useMemo(
    () => sortByOrdem(caracteristicas),
    [caracteristicas]
  );

  // Initial state matching IAuthenticatedPropertyDetails
  const initialForm: IAuthenticatedPropertyDetails = {
    id: '',
    titulo: '',
    cod_imovel: '',
    tipo_imovel: {
      id: 0,
      titulo: '',
      ordem: 0,
      footer: false,
      active: true,
      created_at: new Date(),
      updated_at: new Date(),
      deleted_at: null
    },
    foto_capa: '',
    data_entrada: new Date(),
    area_total: '',
    area_construida: '',
    quartos: undefined,
    suites: undefined,
    banheiros: undefined,
    vagas: undefined,
    is_alugado: false,
    is_vendido: false,
    is_aluguel: false,
    is_venda: false,
    valor_venda: null,
    valor_aluguel: null,
    valor_substituto: null,
    active: true,
    published: false,
    negotiating: false,
    destaque: 0,
    created_at: new Date(),
    updated_at: new Date(),

    cod_imovel_id: null,
    dados_localizacao: {
      imovel_id: 0,
      rua: '',
      numero: '',
      complemento: '',
      bairro: '',
      cep: '',
      nome_condominio: '',
      cidade_id: 0,
      estado_id: 0,
      created_at: new Date(),
      updated_at: new Date()
    },
    dados_comercializacao: {
      imovel_id: 0,
      tipoimovel_id: 0,
      chave_id: null,
      condicao_id: null,
      is_mobiliada: null,
      is_financia: null,
      is_placa: null,
      created_at: new Date(),
      updated_at: new Date(),
      tipo_imovel: {
        id: 0,
        titulo: '',
        ordem: 0,
        footer: false,
        active: true,
        created_at: new Date(),
        updated_at: new Date(),
        deleted_at: null
      }
    },
    caracteristicas: [],
    fotos_galeria: [],
    dados_descricao: {
      descricao: '',
      informacao_adicional: ''
    },
    corretor: {
      id: 0,
      name: '',
      cellphone: '',
      email: '',
      avatar: null,
      creci: ''
    },

    dados_pagamento: {
      is_a_vista: false,
      is_financiamento: false,
      is_permuta: false,
      is_somente_a_vista: false
    },
    dados_privados: {
      imovel_id: 0
    },
    corretor_id: null
  };

  const [formData, setFormData] =
    useState<IAuthenticatedPropertyDetails>(initialForm);
  const canSeeFullRegistrationFields =
    isAdmin ||
    (isBrokerOrSecretary && !isEditingExistingProperty) ||
    (isBrokerOrSecretary &&
      isEditingExistingProperty &&
      !formData.published &&
      Number(formData.corretor_id) === Number(user?.id));
  const upsertDraft = usePropertyFormDraftStore((state) => state.upsertDraft);
  const getDraft = usePropertyFormDraftStore((state) => state.getDraft);
  const clearDraft = usePropertyFormDraftStore((state) => state.clearDraft);
  const draftStorageKey = useMemo(
    () =>
      `${FORM_DRAFT_STORAGE_PREFIX}:` +
      (id && id !== '' ? `edit:${id}` : 'new'),
    [id]
  );
  const formDataRef = useRef<IAuthenticatedPropertyDetails>(initialForm);
  const porcentagemComissaoDisplayRef = useRef<string>('');
  const isDraftHydratedRef = useRef(false);

  // Cidades filtradas pelo estado selecionado (dropdown em cascata)
  const cidadesFiltradas = useMemo(() => {
    const estadoId = formData.dados_localizacao?.estado_id;
    if (!estadoId) return cidadesOptions;
    return cidadesOptions.filter((c) => c.estado_id === estadoId);
  }, [cidadesOptions, formData.dados_localizacao?.estado_id]);

  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [permissionDeniedReason, setPermissionDeniedReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [porcentagemComissaoDisplay, setPorcentagemComissaoDisplay] =
    useState<string>('');
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [showSlowUploadMessage, setShowSlowUploadMessage] = useState(false);
  const [uploadImageCount, setUploadImageCount] = useState<number>(0);
  const slowUploadTimerRef = useRef<number | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  // Editor height preferences — descricao é dinâmica (via RichTextInput),
  // informacao_adicional é fixa em 200px
  const descricaoHeightRef = useRef<string>(
    user?.pref_descricao_height || '300px'
  );
  // Guard: só persiste a altura se o usuário realmente redimensionou nesta sessão.
  // Evita PATCH no mount inicial (e no double-invoke do React StrictMode).
  const hasDescricaoBeenResized = useRef(false);
  const [descricaoHeightForSave, setDescricaoHeightForSave] = useState<string>(
    user?.pref_descricao_height || '300px'
  );
  const lastSavedDescricaoHeightRef = useRef<string>(
    user?.pref_descricao_height || '300px'
  );

  // Callback estável (useCallback) para não recriar o ResizeObserver do RichTextInput
  // a cada re-render do form (ex: usuário digita em qualquer campo).
  const handleDescricaoResize = useCallback(
    (h: string) => {
      hasDescricaoBeenResized.current = true;
      descricaoHeightRef.current = h;
      setDescricaoHeightForSave(h);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    porcentagemComissaoDisplayRef.current = porcentagemComissaoDisplay;
  }, [porcentagemComissaoDisplay]);

  // Busca perfil atualizado do servidor ao entrar na tela
  useEffect(() => {
    fetchMyProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carrega lista de corretores para o seletor (somente admin)
  useEffect(() => {
    if (isAdmin) {
      fetchCorretores();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Sincroniza altura da descrição persistida no perfil
  useEffect(() => {
    const persistedHeight = user?.pref_descricao_height || '300px';
    descricaoHeightRef.current = persistedHeight;
    setDescricaoHeightForSave(persistedHeight);
    lastSavedDescricaoHeightRef.current = persistedHeight;
  }, [user?.pref_descricao_height]);

  // Persiste altura da descrição com debounce, sem depender de beforeunload.
  useEffect(() => {
    if (!hasDescricaoBeenResized.current) return;
    if (descricaoHeightForSave === lastSavedDescricaoHeightRef.current) return;

    const timeout = window.setTimeout(async () => {
      const nextHeight = descricaoHeightRef.current;
      const didSave = await saveEditorPreferences({
        pref_descricao_height: nextHeight
      });
      if (didSave) {
        lastSavedDescricaoHeightRef.current = nextHeight;
      }
    }, DESCRIPTION_HEIGHT_SAVE_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [descricaoHeightForSave, saveEditorPreferences]);

  useEffect(() => {
    if (isEditingExistingProperty) return;

    const draft = getDraft(draftStorageKey);
    if (draft?.formData) {
      setFormData((prev) => ({ ...prev, ...draft.formData }));
      setPorcentagemComissaoDisplay(draft.porcentagemComissaoDisplay || '');
    }
    isDraftHydratedRef.current = true;
  }, [isEditingExistingProperty, draftStorageKey, getDraft]);

  useEffect(() => {
    const loadProperty = async () => {
      if (id) {
        const propertyId = parseInt(id, 10);

        // Verifica se o usuário tem permissão para editar
        const permission = await checkCanEdit(propertyId);
        if (!permission.can_edit) {
          setPermissionDenied(true);
          setPermissionDeniedReason(
            permission.reason ||
              'Você não tem permissão para editar este imóvel.'
          );
          return;
        }

        const propertyDetails =
          await fetchAuthenticatedPropertyDetails(propertyId);
        const draft = getDraft(draftStorageKey);

        console.log(propertyDetails);
        if (propertyDetails) {
          if (
            propertyDetails.dados_descricao &&
            !propertyDetails.dados_descricao.informacao_adicional
          ) {
            propertyDetails.dados_descricao.informacao_adicional = '';
          }
          if (!propertyDetails.cod_imovel_id) {
            propertyDetails.cod_imovel_id =
              codigosOptions.find((c) => c.cod === propertyDetails.cod_imovel)
                ?.id || null;
          }
          // Garante que corretor_id está sempre preenchido (fallback para corretor.id)
          if (!propertyDetails.corretor_id && propertyDetails.corretor?.id) {
            propertyDetails.corretor_id = propertyDetails.corretor.id;
          }
          const mergedData = draft?.formData
            ? ({
                ...propertyDetails,
                ...draft.formData,
                // corretor_id nunca deve ser sobrescrito pelo rascunho
                corretor_id: propertyDetails.corretor_id ?? null
              } as IAuthenticatedPropertyDetails)
            : propertyDetails;

          setFormData((prev) => ({ ...prev, ...mergedData }));
          setPorcentagemComissaoDisplay(
            draft?.porcentagemComissaoDisplay ??
              (mergedData.dados_privados?.porcentagem_comissao != null
                ? String(
                    mergedData.dados_privados.porcentagem_comissao
                  ).replace('.', ',')
                : '')
          );
          setDidFetchProperty(true);
        } else if (draft?.formData) {
          setFormData((prev) => ({ ...prev, ...draft.formData }));
          setPorcentagemComissaoDisplay(draft.porcentagemComissaoDisplay || '');
        }
      }
      isDraftHydratedRef.current = true;
    };
    if (mustFetchProperty) {
      loadProperty();
      setMustFetchProperty(false);
    }
  }, [
    id,
    mustFetchProperty,
    fetchAuthenticatedPropertyDetails,
    checkCanEdit,
    codigosOptions,
    draftStorageKey,
    getDraft
  ]);

  // // Prefill from pending (if any)
  // useEffect(() => {
  //   if (pending) setFormData(pending as IAuthenticatedPropertyDetails);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  // Fetch metadata
  useEffect(() => {
    fetchTiposImovel();
    fetchCondicoesImovel();
    fetchChaves();
    fetchCodigosImovel();
    fetchCidades();
    fetchCaracteristicas();
  }, [
    fetchTiposImovel,
    fetchCondicoesImovel,
    fetchChaves,
    fetchCodigosImovel,
    fetchCidades,
    fetchCaracteristicas
  ]);

  useEffect(() => {
    if (!isDraftHydratedRef.current) return;

    const timeout = window.setTimeout(() => {
      upsertDraft(draftStorageKey, formData, porcentagemComissaoDisplay);
    }, FORM_DRAFT_SAVE_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [draftStorageKey, formData, porcentagemComissaoDisplay, upsertDraft]);

  // Small helpers for nested updates (typed)
  const updateLocalizacao = (partial: Partial<IDadosLocalizacao>) =>
    setFormData((prev) => ({
      ...prev,
      dados_localizacao: { ...prev.dados_localizacao, ...partial }
    }));
  const updateComercializacao = (partial: Partial<IDadosComercializacao>) =>
    setFormData((prev) => ({
      ...prev,
      dados_comercializacao: { ...prev.dados_comercializacao, ...partial }
    }));
  const updatePrivados = (partial: Partial<IDadosPrivados>) =>
    setFormData((prev) => ({
      ...prev,
      dados_privados: {
        imovel_id: prev.dados_privados?.imovel_id || 0,
        ...prev.dados_privados,
        ...partial
      }
    }));
  const updatePagamento = (partial: Partial<IDadosPagamento>) =>
    setFormData((prev) => ({
      ...prev,
      dados_pagamento: {
        is_a_vista: false,
        is_financiamento: false,
        is_permuta: false,
        is_somente_a_vista: false,
        ...prev.dados_pagamento,
        ...partial
      }
    }));

  // Status mapping
  const selectedStatus = useMemo<
    'DISPONIVEL' | 'INATIVO' | 'VENDIDO' | 'ALUGADO'
  >(() => {
    if (formData.is_vendido) return 'VENDIDO';
    if (formData.is_alugado) return 'ALUGADO';
    if (formData.active) return 'DISPONIVEL';
    return 'INATIVO';
  }, [formData.active, formData.is_alugado, formData.is_vendido]);

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value as
      | 'DISPONIVEL'
      | 'INATIVO'
      | 'VENDIDO'
      | 'ALUGADO';
    setFormData((prev) => {
      if (v === 'DISPONIVEL')
        return { ...prev, active: true, is_vendido: false, is_alugado: false };
      if (v === 'VENDIDO')
        return { ...prev, active: false, is_vendido: true, is_alugado: false };
      if (v === 'ALUGADO')
        return { ...prev, active: false, is_vendido: false, is_alugado: true };
      return { ...prev, active: false, is_vendido: false, is_alugado: false };
    });
  };

  // CEP -> prefill street/bairro/cidade_id/estado_id
  const handleCepChange = async (value: string) => {
    updateLocalizacao({ cep: value });
    const digits = value.replace(/\D/g, '');
    if (digits.length === 8) {
      const data = await fetchCep(value);
      if (data) {
        const cidade = cidadesOptions.find(
          (c) => c.nome?.toLowerCase() === data.localidade?.toLowerCase()
        );
        const estado = estadosOptions.find(
          (e) => e.uf?.toLowerCase() === data.uf?.toLowerCase()
        );
        updateLocalizacao({
          rua: data.logradouro || formData.dados_localizacao.rua,
          bairro: data.bairro || formData.dados_localizacao.bairro,
          cidade_id: cidade ? cidade.id : formData.dados_localizacao.cidade_id,
          estado_id: estado ? estado.id : formData.dados_localizacao.estado_id
        });
      }
    }
  };

  // Características
  const toggleCaracteristica = (catId: number) => {
    const meta = caracteristicasOptions.find((c) => c.id === catId);
    if (!meta) return;
    setFormData((prev) => {
      const exists = prev.caracteristicas.some((c) => c.id === catId);
      let next: IPropertyCharacteristic[];
      if (exists) {
        next = prev.caracteristicas.filter((c) => c.id !== catId);
      } else {
        next = [
          ...prev.caracteristicas,
          {
            id: meta.id,
            titulo: meta.titulo,
            ordem: meta.ordem,
            active: meta.active
          }
        ];
      }
      return { ...prev, caracteristicas: next };
    });
  };

  // Callbacks para sincronizar o hook useImageUploader com formData
  const handlePhotosChange = useCallback(
    (photos: typeof formData.fotos_galeria) => {
      setFormData((prev) => {
        const sorted = (photos || []).slice().sort((a, b) => a.ordem - b.ordem);
        const newCover = sorted[0]?.uri ?? prev.foto_capa;
        return { ...prev, fotos_galeria: photos, foto_capa: newCover };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Hook para gerenciar upload de imagens
  const {
    setPhotos: setImagePhotos,
    handleAddImage,
    handleDeleteImage,
    handleSortEnd,
    watermarkPositions,
    watermarkOpacities,
    watermarkSizes,
    handleWatermarkPositionChange,
    handleWatermarkOpacityChange,
    handleWatermarkSizeChange,
    handleRotateImage,
    handleFlipImage
  } = useImageUploader({
    initialPhotos: formData.fotos_galeria,
    imovelId: Number(formData.id) || 0,
    onPhotosChange: handlePhotosChange
  });

  // Sincroniza o estado do hook quando formData é carregado externamente (ex: edição de imóvel)
  useEffect(() => {
    if (didFetchProperty && formData.fotos_galeria) {
      setImagePhotos(formData.fotos_galeria);
    }
  }, [didFetchProperty, formData.fotos_galeria, setImagePhotos]);

  // Callback para alterar a legenda de uma foto na galeria
  const handleCaptionChange = useCallback((index: number, caption: string) => {
    setFormData((prev) => {
      const sorted = (prev.fotos_galeria || [])
        .slice()
        .sort((a, b) => a.ordem - b.ordem);
      if (index < 0 || index >= sorted.length) return prev;
      const targetOrdem = sorted[index].ordem;
      const updatedPhotos = prev.fotos_galeria.map((foto) =>
        foto.ordem === targetOrdem ? { ...foto, legenda: caption } : foto
      );
      return { ...prev, fotos_galeria: updatedPhotos };
    });
  }, []);

  const handleDescricaoChange = useCallback((html: string) => {
    setFormData((prev) => ({
      ...prev,
      dados_descricao: {
        ...(prev.dados_descricao || { informacao_adicional: '' }),
        descricao: html
      }
    }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    setIsSubmitting(true);
    e.preventDefault();
    try {
      const willUploadImages = (formData.fotos_galeria || []).some(
        (p) => !!p.file
      );
      const imageCountToUpload = (formData.fotos_galeria || []).filter(
        (p) => !!p.file
      ).length;
      if (willUploadImages) {
        setIsUploadingImages(true);
        setUploadImageCount(imageCountToUpload);
        setShowSlowUploadMessage(false);
        if (slowUploadTimerRef.current) {
          clearTimeout(slowUploadTimerRef.current);
        }
        slowUploadTimerRef.current = window.setTimeout(() => {
          setShowSlowUploadMessage(true);
        }, 10000);
      }

      let success = false;
      let mutationResult;
      if (isEditingExistingProperty) {
        // Editando
        mutationResult = await patchEditProperty(
          parseInt(id as string, 10),
          formData
        );
      } else {
        // Novo
        mutationResult = await postNewProperty(formData);
      }
      success = mutationResult.success;

      if (!success) {
        let msg =
          'Não foi possível salvar o imóvel. Tente novamente em instantes.';

        if (mutationResult.isImageUploadError) {
          msg =
            'Não foi possível enviar as imagens. Verifique sua conexão e tente novamente. Se o problema persistir, tente reduzir a quantidade/tamanho das imagens e tente novamente.';
        } else if (mutationResult.status === 422) {
          const firstFieldError = mutationResult.errors
            ? Object.values(mutationResult.errors).flat()[0]
            : undefined;

          msg =
            firstFieldError ||
            mutationResult.message ||
            'Não foi possível salvar o imóvel. Verifique os campos informados.';
        } else if (mutationResult.message) {
          msg = mutationResult.message;
        }

        setErrorMessage(msg);
        setErrorOpen(true);
        // continue to finally cleanup below
      } else {
        clearDraft(draftStorageKey);
        await saveEditorPreferences({
          pref_descricao_height: descricaoHeightRef.current
        });
        navigate('/area-do-corretor/imoveis/listagem');
      }
    } catch {
      const willUploadImages = (formData.fotos_galeria || []).some(
        (p) => !!p.file
      );
      const msg = willUploadImages
        ? 'Falha ao enviar as imagens. Verifique sua conexão e tente novamente. Se o problema persistir, tente reduzir a quantidade/tamanho das imagens.'
        : 'Ocorreu um erro ao salvar o imóvel. Tente novamente em instantes.';
      setErrorMessage(msg);
      setErrorOpen(true);
    }
    if (slowUploadTimerRef.current) {
      clearTimeout(slowUploadTimerRef.current);
      slowUploadTimerRef.current = null;
    }
    setShowSlowUploadMessage(false);
    setIsUploadingImages(false);
    setIsSubmitting(false);
  };

  if (isEditingExistingProperty && mustFetchProperty && !didFetchProperty) {
    return null;
  }

  return (
    <AuthenticatedLayout>
      {id ? <h1>EDIÇÃO DE IMÓVEL</h1> : <h1>CADASTRO DE NOVO IMÓVEL</h1>}
      <Styles.RectangleBar />
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <Styles.FormSection>
          <Styles.InputContainer>
            <Styles.Label>Código do Imóvel</Styles.Label>
            <Styles.Select
              style={{ width: '160px' }}
              name="cod_imovel"
              value={
                typeof formData.cod_imovel === 'string'
                  ? formData.cod_imovel
                  : formData.cod_imovel?.titulo
              }
              onChange={(e) => {
                const val = e.target.value;
                const found = codigosOptions.find((c) => c.cod === val);
                setFormData((prev) => ({
                  ...prev,
                  cod_imovel: val,
                  cod_imovel_id: found ? found.id : 0
                }));
              }}>
              <option value="">Selecione</option>
              {codigosOptions.map((c) => (
                <option key={c.id} value={c.cod}>
                  {c.cod}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Título</Styles.Label>
            <Styles.Input
              style={{ width: '700px' }}
              type="text"
              name="titulo"
              value={formData.titulo}
              onChange={(e) =>
                setFormData((p) => ({ ...p, titulo: e.target.value }))
              }
            />
          </Styles.InputContainer>

          <Styles.InputContainer style={{ width: isMobile ? '100%' : '115px' }}>
            <Styles.Label>ID</Styles.Label>
            <Styles.Input type="text" name="id" value={formData.id} disabled />
          </Styles.InputContainer>
        </Styles.FormSection>

        <Styles.FormSection>
          <Styles.InputContainer>
            <Styles.Label>Data de entrada</Styles.Label>
            <Styles.Input
              type="date"
              name="data_entrada"
              value={
                formData.data_entrada
                  ? new Date(formData.data_entrada).toISOString().split('T')[0]
                  : ''
              }
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  data_entrada: e.target.value
                    ? new Date(`${e.target.value}T00:00:00`)
                    : new Date()
                }))
              }
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Área Total (m²)</Styles.Label>
            <Styles.Input
              name="area_total"
              value={formData.area_total || ''}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  area_total: e.target.value
                }))
              }
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Área Construída (m²)</Styles.Label>
            <Styles.Input
              type="text"
              name="area_construida"
              value={formData.area_construida}
              onChange={(e) =>
                setFormData((p) => ({ ...p, area_construida: e.target.value }))
              }
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Quartos</Styles.Label>
            <Styles.Select
              name="quartos"
              value={Number(formData.quartos) || 0}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  quartos: Number(e.target.value || 0)
                }))
              }>
              <option value={undefined}>Selecione</option>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Suítes</Styles.Label>
            <Styles.Select
              name="suites"
              value={Number(formData.suites) || 0}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  suites: Number(e.target.value || 0)
                }))
              }>
              <option value={undefined}>Selecione</option>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Vagas</Styles.Label>
            <Styles.Select
              name="vagas"
              value={Number(formData.vagas) || 0}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  vagas: Number(e.target.value || 0)
                }))
              }>
              <option value={undefined}>Selecione</option>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Banheiros</Styles.Label>
            <Styles.Select
              name="banheiros"
              value={Number(formData.banheiros) || 0}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  banheiros: Number(e.target.value || 0)
                }))
              }>
              <option value={undefined}>Selecione</option>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Tipo do Imóvel</Styles.Label>
            <Styles.Select
              name="tipo_imovel"
              value={
                formData.dados_comercializacao?.tipo_imovel?.titulo ||
                formData.tipo_imovel?.titulo ||
                ''
              }
              onChange={(e) => {
                const titulo = e.target.value;
                const found = tiposOptions.find((t) => t.titulo === titulo);
                setFormData((prev) => ({
                  ...prev,
                  tipo_imovel: {
                    ...(prev.dados_comercializacao?.tipo_imovel || {
                      id: 0,
                      titulo: '',
                      ordem: 0,
                      footer: false,
                      active: true,
                      created_at: new Date(),
                      updated_at: new Date(),
                      deleted_at: null
                    }),
                    id: found ? found.id : 0,
                    titulo: found ? found.titulo : ''
                  },
                  dados_comercializacao: {
                    ...prev.dados_comercializacao,
                    tipoimovel_id: found ? found.id : 0,
                    tipo_imovel: {
                      ...(prev.dados_comercializacao?.tipo_imovel || {
                        id: 0,
                        titulo: '',
                        ordem: 0,
                        footer: false,
                        active: true,
                        created_at: new Date(),
                        updated_at: new Date(),
                        deleted_at: null
                      }),
                      id: found ? found.id : 0,
                      titulo: found ? found.titulo : ''
                    }
                  }
                }));
              }}>
              <option value="">Selecione</option>
              {tiposOptions.map((t) => (
                <option key={t.id} value={t.titulo}>
                  {t.titulo}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>
        </Styles.FormSection>

        <Styles.FormSection>
          <Styles.InputContainer>
            <Styles.Label>Nome do Condomínio</Styles.Label>
            <Styles.Input
              style={{ width: '350px' }}
              type="text"
              name="dados_localizacao.nome_condominio"
              value={formData.dados_localizacao?.nome_condominio || ''}
              onChange={(e) =>
                updateLocalizacao({ nome_condominio: e.target.value })
              }
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Chave</Styles.Label>
            <Styles.Select
              name="dados_comercializacao.chave_id"
              value={
                formData.dados_comercializacao?.chave_id !== null &&
                formData.dados_comercializacao?.chave_id !== undefined
                  ? String(formData.dados_comercializacao.chave_id)
                  : ''
              }
              onChange={(e) => {
                const idNum = Number(e.target.value);
                const found = chavesOptions.find((ch) => ch.id === idNum);
                updateComercializacao({
                  chave_id: Number.isNaN(idNum) ? null : idNum,
                  chave: found
                    ? {
                        titulo: found.titulo,
                        ordem: found.ordem,
                        active: found.active
                      }
                    : undefined
                });
              }}>
              <option value="">Selecione</option>
              {chavesOptions.map((ch) => (
                <option key={ch.id} value={ch.id}>
                  {ch.titulo}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Condições</Styles.Label>
            <Styles.Select
              name="dados_comercializacao.condicao_id"
              value={
                formData.dados_comercializacao?.condicao_id !== null &&
                formData.dados_comercializacao?.condicao_id !== undefined
                  ? String(formData.dados_comercializacao.condicao_id)
                  : ''
              }
              onChange={(e) => {
                const idNum = Number(e.target.value);
                const found = condicoesOptions.find((c) => c.id === idNum);
                updateComercializacao({
                  condicao_id: Number.isNaN(idNum) ? null : idNum,
                  condicao: found
                    ? {
                        titulo: found.titulo,
                        ordem: found.ordem,
                        active: found.active
                      }
                    : undefined
                });
              }}>
              <option value="">Selecione</option>
              {condicoesOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.titulo}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Mobiliado</Styles.Label>
            <Styles.Select
              name="dados_comercializacao.is_mobiliada"
              value={String(
                Boolean(formData.dados_comercializacao?.is_mobiliada)
              )}
              onChange={(e) =>
                updateComercializacao({
                  is_mobiliada: e.target.value === 'true'
                })
              }>
              <option value="false">Não</option>
              <option value="true">Sim</option>
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Financia</Styles.Label>
            <Styles.Select
              name="dados_comercializacao.is_financia"
              value={String(
                Boolean(formData.dados_comercializacao?.is_financia)
              )}
              onChange={(e) =>
                updateComercializacao({
                  is_financia: e.target.value === 'true'
                })
              }>
              <option value="false">Não</option>
              <option value="true">Sim</option>
            </Styles.Select>
          </Styles.InputContainer>
        </Styles.FormSection>

        <Styles.FormSection>
          <Styles.InputContainer>
            <Styles.Label>CEP</Styles.Label>
            <Styles.Input
              type="text"
              name="dados_localizacao.cep"
              value={formData.dados_localizacao?.cep || ''}
              onChange={(e) => handleCepChange(e.target.value)}
              style={{ width: isMobile ? '100%' : '115px' }}
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>UF</Styles.Label>
            <Styles.Select
              style={{ width: '100px' }}
              name="dados_localizacao.estado_id"
              value={String(formData.dados_localizacao?.estado_id || '')}
              onChange={(e) => {
                const novoEstadoId = Number(e.target.value || 0);
                const cidadeAtual = formData.dados_localizacao?.cidade_id;
                const cidadeForaDoEstado = cidadeAtual
                  ? !cidadesOptions.find(
                      (c) =>
                        c.id === cidadeAtual && c.estado_id === novoEstadoId
                    )
                  : false;
                updateLocalizacao({
                  estado_id: novoEstadoId,
                  ...(cidadeForaDoEstado ? { cidade_id: 0 } : {})
                });
              }}>
              <option value="">Selecione</option>
              {estadosOptions.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.uf}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Cidade</Styles.Label>
            <Styles.Select
              style={{ width: '200px' }}
              name="dados_localizacao.cidade_id"
              value={String(formData.dados_localizacao?.cidade_id || '')}
              onChange={(e) => {
                const cidadeId = Number(e.target.value || 0);
                const cidade = cidadesOptions.find((c) => c.id === cidadeId);
                updateLocalizacao({
                  cidade_id: cidadeId,
                  ...(cidade ? { estado_id: cidade.estado_id } : {})
                });
              }}>
              <option value="">Selecione</option>
              {cidadesFiltradas.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Número</Styles.Label>
            <Styles.Input
              style={{ width: isMobile ? '100%' : '85px' }}
              type="text"
              name="dados_localizacao.numero"
              value={formData.dados_localizacao?.numero || ''}
              onChange={(e) => updateLocalizacao({ numero: e.target.value })}
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Complemento</Styles.Label>
            <Styles.Input
              style={{ width: isMobile ? '100%' : '150px' }}
              type="text"
              name="dados_localizacao.complemento"
              value={formData.dados_localizacao?.complemento || ''}
              onChange={(e) =>
                updateLocalizacao({ complemento: e.target.value })
              }
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Bairro</Styles.Label>
            <Styles.Input
              style={{ width: isMobile ? '100%' : '250px' }}
              type="text"
              name="dados_localizacao.bairro"
              value={formData.dados_localizacao?.bairro || ''}
              onChange={(e) => updateLocalizacao({ bairro: e.target.value })}
            />
          </Styles.InputContainer>
        </Styles.FormSection>

        <Styles.FormSection>
          <Styles.InputContainer>
            <Styles.Label>Rua</Styles.Label>
            <Styles.Input
              style={{ width: '600px' }}
              type="text"
              name="dados_localizacao.rua"
              value={formData.dados_localizacao?.rua || ''}
              onChange={(e) => updateLocalizacao({ rua: e.target.value })}
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Condições de Pagamento</Styles.Label>
            <Styles.CheckboxContainer>
              <Styles.CheckboxGridTwoCols>
                <Styles.CheckboxLabel>
                  <Styles.CheckboxInput
                    type="checkbox"
                    checked={formData.dados_pagamento?.is_a_vista === true}
                    onChange={(e) =>
                      updatePagamento({ is_a_vista: e.target.checked })
                    }
                  />
                  À Vista
                </Styles.CheckboxLabel>
                <Styles.CheckboxLabel>
                  <Styles.CheckboxInput
                    type="checkbox"
                    checked={
                      formData.dados_pagamento?.is_financiamento === true
                    }
                    onChange={(e) =>
                      updatePagamento({ is_financiamento: e.target.checked })
                    }
                  />
                  Financiamento
                </Styles.CheckboxLabel>
                <Styles.CheckboxLabel>
                  <Styles.CheckboxInput
                    type="checkbox"
                    checked={formData.dados_pagamento?.is_permuta === true}
                    onChange={(e) =>
                      updatePagamento({ is_permuta: e.target.checked })
                    }
                  />
                  Aceita Permuta
                </Styles.CheckboxLabel>
                <Styles.CheckboxLabel>
                  <Styles.CheckboxInput
                    type="checkbox"
                    checked={
                      formData.dados_pagamento?.is_somente_a_vista === true
                    }
                    onChange={(e) =>
                      updatePagamento({ is_somente_a_vista: e.target.checked })
                    }
                  />
                  Somente à Vista
                </Styles.CheckboxLabel>
              </Styles.CheckboxGridTwoCols>
            </Styles.CheckboxContainer>
          </Styles.InputContainer>
        </Styles.FormSection>

        <Styles.FormSection>
          <Styles.InputContainer>
            <Styles.Label>Valor Venda</Styles.Label>
            <CurrencyMaskedInput
              name="valor_venda"
              value={Number(formData.valor_venda ?? 0)}
              onChange={(value) =>
                setFormData((p) => ({
                  ...p,
                  valor_venda: value
                }))
              }
            />
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Valor Aluguel</Styles.Label>
            <CurrencyMaskedInput
              name="valor_aluguel"
              value={Number(formData.valor_aluguel ?? 0)}
              onChange={(value) =>
                setFormData((p) => ({
                  ...p,
                  valor_aluguel: value
                }))
              }
            />
          </Styles.InputContainer>

          {canSeeFullRegistrationFields && (
            <Styles.InputContainer>
              <Styles.Label>Valor Proprietário</Styles.Label>
              <CurrencyMaskedInput
                name="dados_privados.valor_proprietario"
                value={Number(formData.dados_privados?.valor_proprietario ?? 0)}
                onChange={(value) =>
                  updatePrivados({
                    valor_proprietario: value
                  })
                }
              />
            </Styles.InputContainer>
          )}

          <Styles.InputContainer>
            <Styles.Label>Valor Substituto</Styles.Label>
            <CurrencyMaskedInput
              name="valor_substituto"
              value={Number(formData.valor_substituto ?? 0)}
              onChange={(value) =>
                setFormData((p) => ({
                  ...p,
                  valor_substituto: value
                }))
              }
            />
          </Styles.InputContainer>

          {canSeeFullRegistrationFields && (
            <Styles.InputContainer>
              <Styles.Label>Comissão</Styles.Label>
              <CurrencyMaskedInput
                name="dados_privados.comissao"
                value={Number(formData.dados_privados?.comissao ?? 0)}
                onChange={(value) => updatePrivados({ comissao: value })}
              />
            </Styles.InputContainer>
          )}

          {canSeeFullRegistrationFields && (
            <Styles.InputContainer>
              <Styles.Label>% Comissão</Styles.Label>
              <Styles.Input
                type="text"
                inputMode="decimal"
                name="dados_privados.porcentagem_comissao"
                value={porcentagemComissaoDisplay}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Aceita dígitos, vírgula ou ponto como separador decimal
                  if (/^(\d*[.,]?\d*)$/.test(raw)) {
                    // Exibe com vírgula
                    const displayed = raw.replace('.', ',');
                    setPorcentagemComissaoDisplay(displayed);
                    // Para parsear, converte vírgula em ponto
                    const normalized = displayed.replace(',', '.');
                    if (normalized !== '' && !normalized.endsWith('.')) {
                      updatePrivados({
                        porcentagem_comissao: parseFloat(normalized)
                      });
                    } else if (normalized === '') {
                      updatePrivados({ porcentagem_comissao: undefined });
                    }
                  }
                }}
              />
            </Styles.InputContainer>
          )}
        </Styles.FormSection>

        <Styles.FormSection>
          {canSeeFullRegistrationFields && (
            <Styles.InputContainer>
              <Styles.Label>Valor Condomínio</Styles.Label>
              <CurrencyMaskedInput
                name="dados_privados.valor_condominio"
                value={Number(formData.dados_privados?.valor_condominio ?? 0)}
                onChange={(value) =>
                  updatePrivados({
                    valor_condominio: value
                  })
                }
              />
            </Styles.InputContainer>
          )}

          {canSeeFullRegistrationFields && (
            <Styles.InputContainer>
              <Styles.Label>Valor IPTU</Styles.Label>
              <CurrencyMaskedInput
                name="dados_privados.valor_iptu"
                value={Number(formData.dados_privados?.valor_iptu ?? 0)}
                onChange={(value) => updatePrivados({ valor_iptu: value })}
              />
            </Styles.InputContainer>
          )}

          <Styles.InputContainer>
            <Styles.Label>Placa</Styles.Label>
            <Styles.Select
              style={{ width: '100px' }}
              name="dados_comercializacao.is_placa"
              value={String(Boolean(formData.dados_comercializacao?.is_placa))}
              onChange={(e) =>
                updateComercializacao({ is_placa: e.target.value === 'true' })
              }>
              <option value="false">Não</option>
              <option value="true">Sim</option>
            </Styles.Select>
          </Styles.InputContainer>

          <Styles.InputContainer>
            <Styles.Label>Disponível Para</Styles.Label>
            <Styles.CheckboxContainer>
              <Styles.CheckboxRow>
                <Styles.CheckboxLabel>
                  <Styles.CheckboxInput
                    type="checkbox"
                    name="is_venda"
                    checked={formData.is_venda === true}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_venda: e.target.checked
                      }))
                    }
                  />
                  Venda
                </Styles.CheckboxLabel>
                <Styles.CheckboxLabel>
                  <Styles.CheckboxInput
                    type="checkbox"
                    name="is_aluguel"
                    checked={formData.is_aluguel === true}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_aluguel: e.target.checked
                      }))
                    }
                  />
                  Aluguel
                </Styles.CheckboxLabel>
              </Styles.CheckboxRow>
            </Styles.CheckboxContainer>
          </Styles.InputContainer>
        </Styles.FormSection>

        <Styles.FormSection>
          <Styles.InputContainer>
            <Styles.Label>Características do Imóvel</Styles.Label>
            <Styles.CheckboxContainer>
              <Styles.CheckboxGridGroup>
                {caracteristicasOptions.map((cat) => (
                  <Styles.CheckboxLabel key={cat.id}>
                    <Styles.CheckboxInput
                      type="checkbox"
                      checked={formData.caracteristicas.some(
                        (c) => c.id === cat.id
                      )}
                      onChange={() => toggleCaracteristica(cat.id)}
                    />
                    {cat.titulo}
                  </Styles.CheckboxLabel>
                ))}
              </Styles.CheckboxGridGroup>
            </Styles.CheckboxContainer>
          </Styles.InputContainer>
        </Styles.FormSection>

        <Styles.FormColumn>
          <Styles.Label>Descrição</Styles.Label>
          <RichTextInput
            initialHeight={descricaoHeightRef.current}
            value={formData.dados_descricao?.descricao}
            onChange={handleDescricaoChange}
            onResize={handleDescricaoResize}
          />
        </Styles.FormColumn>

        {canSeeFullRegistrationFields &&
          formData.dados_descricao.informacao_adicional != null && (
            <Styles.FormColumn style={{ marginTop: '20px' }}>
              <Styles.Label>Informação Adicional</Styles.Label>
              <Styles.TextArea
                ref={textAreaRef}
                name="descricao.informacao_adicional"
                value={formData.dados_descricao?.informacao_adicional}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    dados_descricao: {
                      ...(prev.dados_descricao || { descricao: '' }),
                      informacao_adicional: e.target.value
                    }
                  }))
                }
                style={{ height: '200px' }}
              />
            </Styles.FormColumn>
          )}

        {canSeeFullRegistrationFields && (
          <Styles.OwnerSection>
            <Styles.RectangleBar
              style={{
                marginTop: '30px',
                paddingTop: '10px',
                backgroundColor: '#D9D9D9'
              }}
            />
            <h1>DADOS DO PROPRIETÁRIO</h1>
            <Styles.OwnerInputRow>
              <Styles.InputContainer>
                <Styles.Label>Nome</Styles.Label>
                <Styles.Input
                  style={{ width: '500px' }}
                  type="text"
                  placeholder="Nome"
                  name="dados_privados.nome_proprietario"
                  value={formData.dados_privados?.nome_proprietario || ''}
                  onChange={(e) =>
                    updatePrivados({ nome_proprietario: e.target.value })
                  }
                />
              </Styles.InputContainer>
              <Styles.InputContainer>
                <Styles.Label>E-mail</Styles.Label>
                <Styles.Input
                  type="email"
                  placeholder="E-mail"
                  style={{ width: '440px' }}
                  name="dados_privados.email_proprietario"
                  value={formData.dados_privados?.email_proprietario || ''}
                  onChange={(e) =>
                    updatePrivados({ email_proprietario: e.target.value })
                  }
                />
              </Styles.InputContainer>
            </Styles.OwnerInputRow>
            <Styles.OwnerInputRow>
              <Styles.InputContainer>
                <Styles.Label>RG</Styles.Label>
                <Styles.Input
                  type="text"
                  placeholder="RG"
                  name="dados_privados.rg_proprietario"
                  value={formData.dados_privados?.rg_proprietario || ''}
                  onChange={(e) =>
                    updatePrivados({ rg_proprietario: e.target.value })
                  }
                />
              </Styles.InputContainer>
              <Styles.InputContainer>
                <Styles.Label>CPF</Styles.Label>
                <Styles.Input
                  type="text"
                  placeholder="CPF"
                  name="dados_privados.cpf_proprietario"
                  value={formData.dados_privados?.cpf_proprietario || ''}
                  onChange={(e) =>
                    updatePrivados({ cpf_proprietario: e.target.value })
                  }
                />
              </Styles.InputContainer>
              <Styles.InputContainer>
                <Styles.Label>Tel. Residencial</Styles.Label>
                <Styles.Input
                  type="text"
                  placeholder="Tel. Residencial"
                  name="dados_privados.telefone_residencial_proprietario"
                  value={
                    formData.dados_privados
                      ?.telefone_residencial_proprietario || ''
                  }
                  onChange={(e) =>
                    updatePrivados({
                      telefone_residencial_proprietario: e.target.value
                    })
                  }
                />
              </Styles.InputContainer>
              <Styles.InputContainer>
                <Styles.Label>Tel. Comercial</Styles.Label>
                <Styles.Input
                  type="text"
                  placeholder="Tel. Comercial"
                  name="dados_privados.telefone_comercial_proprietario"
                  value={
                    formData.dados_privados?.telefone_comercial_proprietario ||
                    ''
                  }
                  onChange={(e) =>
                    updatePrivados({
                      telefone_comercial_proprietario: e.target.value
                    })
                  }
                />
              </Styles.InputContainer>
              <Styles.InputContainer>
                <Styles.Label>Celular 1</Styles.Label>
                <Styles.Input
                  type="text"
                  placeholder="Celular 1"
                  name="dados_privados.celular_primario_proprietario"
                  value={
                    formData.dados_privados?.celular_primario_proprietario || ''
                  }
                  onChange={(e) =>
                    updatePrivados({
                      celular_primario_proprietario: e.target.value
                    })
                  }
                />
              </Styles.InputContainer>
              <Styles.InputContainer>
                <Styles.Label>Celular 2</Styles.Label>
                <Styles.Input
                  type="text"
                  placeholder="Celular 2"
                  name="dados_privados.celular_secundario_proprietario"
                  value={
                    formData.dados_privados?.celular_secundario_proprietario ||
                    ''
                  }
                  onChange={(e) =>
                    updatePrivados({
                      celular_secundario_proprietario: e.target.value
                    })
                  }
                />
              </Styles.InputContainer>
            </Styles.OwnerInputRow>
          </Styles.OwnerSection>
        )}

        <ImageUploader
          photos={formData.fotos_galeria || []}
          onAddImages={handleAddImage}
          onDeleteImage={handleDeleteImage}
          onSortEnd={handleSortEnd}
          watermarkPositions={watermarkPositions}
          watermarkOpacities={watermarkOpacities}
          watermarkSizes={watermarkSizes}
          onWatermarkPositionChange={handleWatermarkPositionChange}
          onWatermarkOpacityChange={handleWatermarkOpacityChange}
          onWatermarkSizeChange={handleWatermarkSizeChange}
          onRotateImage={handleRotateImage}
          onFlipImage={handleFlipImage}
          onCaptionChange={handleCaptionChange}
        />

        {canSeeFullRegistrationFields && (
          <Styles.StatusSection>
            <Styles.StatusRow>
              <Styles.FieldBlock>
                <Styles.Label>STATUS</Styles.Label>
                <Styles.Select
                  name="status"
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  style={{ width: '220px' }}>
                  <option value="DISPONIVEL">Ativo</option>
                  <option value="INATIVO">Inativo</option>
                  <option value="VENDIDO">Vendido</option>
                  <option value="ALUGADO">Alugado</option>
                </Styles.Select>
              </Styles.FieldBlock>
              <Styles.FieldBlock>
                <Styles.Label>DESTAQUE</Styles.Label>
                <Styles.Switch>
                  <Styles.SwitchInput
                    type="checkbox"
                    checked={Boolean(formData.destaque)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        destaque: e.target.checked ? 1 : 0
                      }))
                    }
                  />
                  <Styles.SwitchSlider />
                </Styles.Switch>
              </Styles.FieldBlock>
              <Styles.FieldBlock>
                <Styles.Label>EM NEGOCIAÇÃO</Styles.Label>
                <Styles.Switch>
                  <Styles.SwitchInput
                    type="checkbox"
                    checked={Boolean(formData.negotiating)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        negotiating: e.target.checked
                      }))
                    }
                  />
                  <Styles.SwitchSlider />
                </Styles.Switch>
              </Styles.FieldBlock>
            </Styles.StatusRow>
          </Styles.StatusSection>
        )}
        {isAdmin && (
          <Styles.StatusSection>
            <Styles.StatusRow>
              <Styles.FieldBlock>
                <Styles.Label>CORRETOR RESPONSÁVEL</Styles.Label>
                <Styles.Select
                  name="corretor_id"
                  value={formData.corretor_id ?? ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      corretor_id: e.target.value
                        ? Number(e.target.value)
                        : null
                    }))
                  }
                  style={{ width: '220px' }}>
                  <option value="">Selecione</option>
                  {corretores.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Styles.Select>
              </Styles.FieldBlock>
            </Styles.StatusRow>
          </Styles.StatusSection>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <Styles.Button type="submit">SALVAR</Styles.Button>
          <Styles.CancelButton type="button" onClick={() => navigate(-1)}>
            CANCELAR
          </Styles.CancelButton>
        </div>
      </form>
      <Modal
        open={permissionDenied}
        aria-labelledby="permission-modal-title"
        aria-describedby="permission-modal-description">
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            width: { xs: '90%', sm: 480 }
          }}>
          <Typography id="permission-modal-title" variant="h6" sx={{ mb: 1 }}>
            Acesso negado
          </Typography>
          <Typography
            id="permission-modal-description"
            variant="body2"
            sx={{ mb: 2 }}>
            {permissionDeniedReason}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}>
              Voltar
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal
        open={errorOpen}
        onClose={() => setErrorOpen(false)}
        aria-labelledby="error-modal-title"
        aria-describedby="error-modal-description">
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            width: { xs: '90%', sm: 480 }
          }}>
          <Typography id="error-modal-title" variant="h6" sx={{ mb: 1 }}>
            Erro ao salvar
          </Typography>
          <Typography
            id="error-modal-description"
            variant="body2"
            sx={{ mb: 2 }}>
            {errorMessage}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setErrorOpen(false)}>
              Fechar
            </Button>
          </Box>
        </Box>
      </Modal>
      <Backdrop
        open={isSubmitting}
        sx={{
          backgroundColor: '#ffffff88',
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}>
          <CircularProgress size="5rem" />
          {isUploadingImages && (
            <>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Enviando {uploadImageCount ? ` (${uploadImageCount})` : ''}{' '}
                imagens...
              </Typography>
              {showSlowUploadMessage && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ maxWidth: 520 }}>
                  Esta operação pode demorar dependendo da conexão de internet
                  com o servidor.
                </Typography>
              )}
            </>
          )}
        </Box>
      </Backdrop>
    </AuthenticatedLayout>
  );
};

export default NewRegisterScreen;
